import { LogOut, Settings } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CgSpinner } from 'react-icons/cg'
import { HiOutlineClipboardCopy } from 'react-icons/hi'
import { LuKey } from 'react-icons/lu'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuthStore, type User } from '@/stores/auth.store'
import { useUiStore } from '@/stores/ui.store'
import { clearDatadogUser } from '@/utils/datadog'
import getAccessToken from '@/utils/getAccessToken'
import { useMsal } from '@azure/msal-react'
import { useQueryClient } from '@tanstack/react-query'

const User = ({
  user,
  userLoading,
}: {
  user?: User | null
  userLoading: boolean
}) => (
  <div className='pointer-events-none flex items-center py-4'>
    <div className='flex min-w-0 flex-1 flex-col pl-4'>
      <p className='text-text-primary truncate text-sm font-medium'>
        {userLoading ? (
          <Skeleton className='mb-3 h-4 w-32' />
        ) : (
          <span>
            {user?.givenName} {user?.surname}
          </span>
        )}
      </p>
      <p className='text-text-tertiary line-clamp-2 text-sm'>
        {userLoading ? <Skeleton className='h-4 w-32' /> : user?.jobTitle}
      </p>
    </div>
  </div>
)

const menuItemCls =
  'flex cursor-pointer items-center gap-2 text-text-secondary hover:bg-neutral-dimmed-heavy hover:text-text-primary focus:bg-neutral-dimmed-heavy focus:text-text-primary'

const Spinner = () => (
  <>
    <CgSpinner className='ml-2 h-5 w-5 animate-spin' aria-hidden='true' />
    <span className='sr-only'>Loading…</span>
  </>
)

const ResetAuthTokenButton = () => {
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  const handle = async () => {
    setLoading(true)
    await getAccessToken(true)
    queryClient.refetchQueries()
    setLoading(false)
  }

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault()
        handle()
      }}
      className={menuItemCls}
    >
      <LuKey className='h-4 w-4' aria-hidden='true' />
      <span>Refresh Auth Token</span>
      {loading && <Spinner />}
    </DropdownMenuItem>
  )
}

const CopyAuthTokenButton = () => {
  const [loading, setLoading] = useState(false)

  const handle = async () => {
    setLoading(true)
    try {
      const token = await getAccessToken()
      if (token) await navigator.clipboard.writeText(token)
    } catch (e) {
      console.error('Failed to copy auth token:', e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault()
        handle()
      }}
      className={menuItemCls}
    >
      <HiOutlineClipboardCopy className='h-4 w-4' aria-hidden='true' />
      <span>Copy Auth Token</span>
      {loading && <Spinner />}
    </DropdownMenuItem>
  )
}

const LogOutButton = () => {
  const { instance } = useMsal()
  const handleLogout = () => {
    clearDatadogUser()
    instance.logoutRedirect()
  }

  return (
    <DropdownMenuItem
      onClick={handleLogout}
      className='text-text-secondary hover:bg-neutral-dimmed-heavy focus:bg-neutral-dimmed-heavy flex cursor-pointer items-center gap-2 hover:text-red-400 focus:text-red-400'
    >
      <LogOut className='h-4 w-4' />
      <span>Logout</span>
    </DropdownMenuItem>
  )
}

const UserMenu = () => {
  const navigate = useNavigate()
  const user = useAuthStore((store) => store.user)
  const userLoading = useAuthStore((store) => store.userLoading)
  const developerMode = useUiStore((store) => store.developerMode)
  const permissions = useAuthStore((s) => s.permissions)

  const userRole = permissions?.role
  const canAccessControlCenter =
    userRole === 'SUPER_ADMIN' || userRole === 'ADMIN'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className='focus:ring-0 focus:outline-none'>
        <Avatar className='h-10 w-10 hover:cursor-pointer'>
          {userLoading ? (
            <Skeleton className='h-10 w-10 rounded-full' />
          ) : (
            <>
              <AvatarImage src={user?.avatarUrl as string | undefined} />
              <AvatarFallback className='bg-[#016772]'>
                {user?.initials}
              </AvatarFallback>
            </>
          )}
        </Avatar>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className='border-border-main bg-neutral w-56 rounded-lg border p-2 shadow-lg'
        align='start'
      >
        <User user={user} userLoading={userLoading} />

        <DropdownMenuSeparator className='bg-border-main' />

        {canAccessControlCenter && (
          <DropdownMenuItem
            onClick={() => navigate('/control-center/alerts')}
            className={menuItemCls}
          >
            <Settings className='h-4 w-4' />
            <span>Control Center</span>
          </DropdownMenuItem>
        )}

        <ResetAuthTokenButton />

        {developerMode && <CopyAuthTokenButton />}

        <LogOutButton />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserMenu
